// USDD Academy — single-hub analytics layer.
//
// Architecture: **Google Tag Manager is the only script we load**. GA4,
// Meta Pixel, TikTok Pixel, Google Ads conversions etc. are configured
// inside GTM, not in this code. We just push semantically named events
// to `window.dataLayer` and let GTM fan them out.
//
// Consent Mode v2 is initialised BEFORE GTM loads, with all consent
// signals defaulted to denied, so no tracking fires until the user
// chooses on the cookie banner.

import { getStoredCampaign } from "./campaign";

const CONSENT_KEY = "usdd_consent_v1";

// ── Consent helpers ──────────────────────────────────────────────────────
export const DEFAULT_CONSENT = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  functionality_storage: "granted", // required for cookie banner itself
  security_storage: "granted",
};

export function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function persistConsent(consent) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      ...consent,
      decided_at: new Date().toISOString(),
    }));
  } catch { /* ignore quota errors */ }
  applyConsent(consent);
}

export function applyConsent(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Push raw object first so GTM-side custom tags can react to it.
  window.dataLayer.push({ event: "consent_update", consent });
  // Use gtag('consent','update') so Google's native Consent Mode v2 receives it.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", consent);
  } else {
    // Stand-in gtag — GTM will replace this once loaded.
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("consent", "update", consent);
  }
}

// ── GTM loader ───────────────────────────────────────────────────────────
// GTM is now loaded by the standard Google snippet hard-coded in
// `public/index.html` (with Consent Mode v2 defaults set inline BEFORE
// the gtm.js script tag — Google's recommended implementation). This
// function is kept as a no-op for backwards compatibility with any
// callers that still invoke it (e.g. AnalyticsProvider on mount).
export function loadAnalytics() {
  if (typeof window === "undefined") return;
  if (window.__usdd_gtm_loaded) return;
  window.__usdd_gtm_loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };

  // If a stored consent decision exists, re-apply it as an UPDATE so any
  // GTM tags loaded during this session see the granted state.
  const stored = getStoredConsent();
  if (stored) {
    const merged = { ...DEFAULT_CONSENT, ...stored };
    window.gtag("consent", "update", merged);
  }
}

// ── Event dispatch ───────────────────────────────────────────────────────
// One canonical helper used across the entire app. Always pushes to
// dataLayer; GTM decides whether the event reaches GA4 / Meta / TikTok
// based on the user's consent state.
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || !name) return;
  const campaign = getStoredCampaign();
  const enriched = {
    event: name,
    ...params,
    ...(campaign.utm || {}),
    campaign_focus: campaign.focus || undefined,
    campaign_intent: campaign.intent || undefined,
    referral_ref: campaign.ref || undefined,
  };
  window.dataLayer = window.dataLayer || [];
  // Dedupe identical purchase/begin_checkout events fired twice within 1.5s
  // (defends against double-mount in React StrictMode + idempotent webhook
  // bounces that some pages handle client-side).
  const dedupeKey = name + ":" + (params.transaction_id || params.sku || params.order_id || "");
  const now = Date.now();
  window.__usdd_last_events = window.__usdd_last_events || {};
  const last = window.__usdd_last_events[dedupeKey];
  if (last && now - last < 1500) return;
  window.__usdd_last_events[dedupeKey] = now;
  window.dataLayer.push(enriched);
}

export function trackPageView(path) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "page_view", page_path: path });
}

// Convenience export — submit forms can call `attachCampaign(payload)` to
// merge UTM + focus + intent + ref into the request body sent to backend.
export function attachCampaign(payload = {}) {
  const c = getStoredCampaign();
  const utm = c.utm || {};
  const has = Object.keys(utm).length > 0 || c.focus || c.intent || c.ref;
  if (!has) return payload;
  return {
    ...payload,
    utm: {
      ...utm,
      focus: c.focus || undefined,
      intent: c.intent || undefined,
      ref: c.ref || undefined,
      landed_at: c.landed_at || undefined,
      landing_path: c.path || undefined,
    },
  };
}
